import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import XIcon from "@mui/icons-material/X";
import { DialogContent, TextField } from "@mui/material";
import { type FC, useMemo } from "react";

import { RichTooltip } from "~/components/UI/RichTooltip";
import { ChartDialog } from "~/feature/chart/components/ChartDialog";
import { ToggleRow } from "~/feature/chart/components/ToggleRow";
import { buildMarketGroups } from "~/feature/chart/utils";
import type { IMarket, IOutcomeConsensus } from "~/types/event";

import { XShareMarketPicker } from "./components/XShareMarketPicker";
import { XSharePreview } from "./components/XSharePreview";
import {
  X_COUNTER_HINT,
  X_MENTION,
  X_SHARE_DIALOG_SUBTITLE,
  X_SHARE_DIALOG_TITLE,
} from "./constants";
import { usePrewarmSharePage } from "./hooks/usePrewarmSharePage";
import { useXShare, XShareProvider } from "./providers/XShareProvider";
import type { IXShareEvent, IXShareSelection } from "./types";
import { buildXIntentUrl } from "./utils/buildXIntentUrl";
import { getXShareForm } from "./utils/getXShareForm";
import { getXShareCardSrc, getXShareUrl } from "./utils/getXShareLinks";
import { getXShareSummary } from "./utils/getXShareSummary";
import * as Styled from "./XShareDialog.styles";

interface IXShareDialogProps {
  open: boolean;
  onClose: () => void;
  event: IXShareEvent;
  markets: IMarket[];
  /** The markets endpoint's `outcomes[]`: the PH Consensus a whole-outcome post shares. */
  outcomes: readonly IOutcomeConsensus[];
  defaultSelection: IXShareSelection | null;
}

interface IXShareDialogContentProps {
  event: IXShareEvent;
  marketGroups: Map<string, IMarket[]>;
  outcomes: readonly IOutcomeConsensus[];
}

const XShareDialogContent: FC<IXShareDialogContentProps> = ({
  event,
  marketGroups,
  outcomes,
}) => {
  const {
    selection,
    setSelection,
    draft,
    setDraft,
    mention,
    setMention,
    hashtagsEnabled,
    setHashtagsEnabled,
    hashtags,
    setHashtags,
    chartCard,
    setChartCard,
  } = useXShare();
  const group = useMemo(
    () => (selection ? (marketGroups.get(selection.groupId) ?? []) : []),
    [marketGroups, selection]
  );
  const outcome = useMemo(
    () =>
      selection
        ? (outcomes.find(
            ({ marketGroupKey }) => marketGroupKey === selection.groupId
          ) ?? null)
        : null,
    [outcomes, selection]
  );
  const summary = useMemo(
    () =>
      selection
        ? getXShareSummary(group, outcome, selection, event.title)
        : null,
    [event.title, group, outcome, selection]
  );
  const form = getXShareForm({
    summary,
    hasLink: Boolean(selection),
    draft,
    mention,
    hashtagsEnabled,
    hashtags,
  });
  const shareUrl = selection
    ? getXShareUrl(event.slug, selection, group, chartCard)
    : "";
  const cardSrc =
    selection && chartCard ? getXShareCardSrc(group, selection) : null;

  usePrewarmSharePage(shareUrl);
  const handlePost = () => {
    window.open(
      buildXIntentUrl({
        text: form.postText,
        url: shareUrl,
        hashtags: form.activeHashtags,
      }),
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <DialogContent>
      <Styled.Columns>
        <Styled.Controls>
          <Styled.Field>
            <Styled.FieldLabel component="p" variant="caption">
              Market
            </Styled.FieldLabel>
            <XShareMarketPicker
              marketGroups={marketGroups}
              value={selection}
              onChange={setSelection}
            />
          </Styled.Field>

          <Styled.Field>
            <Styled.FieldLabel component="p" variant="caption">
              Post text
            </Styled.FieldLabel>
            <TextField
              multiline
              minRows={6}
              fullWidth
              value={form.text}
              onChange={({ target }) => setDraft(target.value)}
              slotProps={{ htmlInput: { "aria-label": "Post text" } }}
            />
            <Styled.CounterRow>
              <Styled.Counter variant="caption" $over={form.isOverLimit}>
                {form.length} / {form.limit}
              </Styled.Counter>
              <RichTooltip title={X_COUNTER_HINT} actionText="" maxWidth={280}>
                <Styled.InfoIcon>
                  <InfoOutlinedIcon fontSize="inherit" color="disabled" />
                </Styled.InfoIcon>
              </RichTooltip>
            </Styled.CounterRow>
          </Styled.Field>

          <ToggleRow
            label={`Mention ${X_MENTION}`}
            checked={mention}
            onChange={setMention}
          />
          <ToggleRow
            label="Include hashtags"
            checked={hashtagsEnabled}
            onChange={setHashtagsEnabled}
          />
          <Styled.HashtagsField
            size="small"
            fullWidth
            disabled={!hashtagsEnabled}
            value={hashtags}
            onChange={({ target }) => setHashtags(target.value)}
            slotProps={{ htmlInput: { "aria-label": "Hashtags" } }}
          />
          <ToggleRow
            label="Include chart card"
            checked={chartCard}
            onChange={setChartCard}
          />
        </Styled.Controls>
        <Styled.Share>
          <XSharePreview
            text={form.postText}
            hashtags={form.activeHashtags}
            url={shareUrl}
            cardSrc={cardSrc}
          />
          <Styled.PostButton
            variant="contained"
            fullWidth
            startIcon={<XIcon />}
            disabled={!form.postText}
            onClick={handlePost}
          >
            Post to X
          </Styled.PostButton>
        </Styled.Share>
      </Styled.Columns>
    </DialogContent>
  );
};

export const XShareDialog: FC<IXShareDialogProps> = ({
  open,
  onClose,
  event,
  markets,
  outcomes,
  defaultSelection,
}) => {
  const marketGroups = useMemo(() => buildMarketGroups(markets), [markets]);

  return (
    <ChartDialog
      open={open}
      onClose={onClose}
      title={X_SHARE_DIALOG_TITLE}
      subtitle={X_SHARE_DIALOG_SUBTITLE}
    >
      <XShareProvider defaultSelection={defaultSelection}>
        <XShareDialogContent
          event={event}
          marketGroups={marketGroups}
          outcomes={outcomes}
        />
      </XShareProvider>
    </ChartDialog>
  );
};
